import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import FormFieldTsoPq from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { parseSubstationDevices, TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { Box, Stack } from '@mui/material';
import { useGetAggregationIntervalGetAllQuery } from '@redux/apiSlices/aggregationIntervalApi';
import {
  useLazyPostImportOperationStartForceFullImportQuery,
  useLazyPostImportOperationStartPartialImportQuery,
  usePostImportOperationRetryImportValidationMutation,
} from '@redux/apiSlices/importOperationApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { mandatoryIntervalOption, mandatorySelection } from '@utils/validationSchema';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object } from 'zod';
import {
  StyledBoxContainer,
  StyledBoxContainerButtons,
  StyledUploadDetailValidityCheckAdditionalContentErrorDetailsContent,
  StyledUploadDetailValidityCheckAdditionalContentErrorDetailsMissingWrapper,
  ValidityCheckDetailsContainer,
} from '../StyledUploadDataComponents';

type ValidityCheckDetailsProps = {
  errorReason: { title: string; content: string; id?: number } | undefined;
  validationErrorDetails: string | undefined;
  selectedMeasurementDeviceId: number | null;
  selectedAggregationIntervalId: number | null;
  onSubmitHandler: (measurementDeviceId: number | null, aggregationIntervalId: number | null) => void | Promise<void>;
};

const findTreeNodeByEntityId = (
  nodes: TreeListItemTsoPqProps[],
  entityId: number | null | undefined
): TreeListItemTsoPqProps | null => {
  if (!entityId) {
    return null;
  }

  for (const node of nodes) {
    if (node.entityId === entityId) {
      return node;
    }

    if (node.options?.length) {
      const nestedNode = findTreeNodeByEntityId(node.options, entityId);
      if (nestedNode) {
        return nestedNode;
      }
    }
  }

  return null;
};

const findTreeNodeById = (nodes: TreeListItemTsoPqProps[], nodeId: string): TreeListItemTsoPqProps | null => {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }

    if (node.options?.length) {
      const nestedNode = findTreeNodeById(node.options, nodeId);
      if (nestedNode) {
        return nestedNode;
      }
    }
  }

  return null;
};

const FormContent = ({
  register,
  watch,
  setValue,
  errors,
  t,
  trigger,
  customProps,
}: any) => {
  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const { data: tsos } = useGetTsoGetAllQuery();
  const { data: aggregationIntervals } = useGetAggregationIntervalGetAllQuery();

  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[];
  }>({});

  const loggedInTso = tsos?.externalDtos?.find((t) => t.isLoggedInUsersTso);
  const loggedInTsoId = loggedInTso?.id;
  const selectedDeviceTreeId = useMemo(() => {
    if (!loggedInTsoId) {
      return undefined;
    }

    const selectedDeviceNode = findTreeNodeByEntityId(
      tsoDeviceOptions[loggedInTsoId] ?? [],
      customProps?.selectedMeasurementDeviceId
    );

    return selectedDeviceNode ? [selectedDeviceNode.id] : undefined;
  }, [customProps?.selectedMeasurementDeviceId, loggedInTsoId, tsoDeviceOptions]);

  useEffect(() => {
    if (!loggedInTsoId || tsoDeviceOptions[loggedInTsoId]) return;

    const fetchDevices = async () => {
      try {
        const result = await triggerGetDeviceHierarchy(loggedInTsoId, true);
        const rawTree = result.data?.externalDtos;

        if (rawTree) {
          const parsedTree = parseSubstationDevices(rawTree);
          if (parsedTree) {
            setTsoDeviceOptions((prev) => ({ ...prev, [loggedInTsoId]: parsedTree }));
          }
        }
      } catch (error) {
        console.error('Error getting device hierarchy:', error);
      }
    };
    fetchDevices();
  }, [loggedInTsoId, triggerGetDeviceHierarchy, tsoDeviceOptions]);

  const deviceChangeHandler = (values: string[]) => {
    const selectedTreeId = values[0];
    const selectedNode = selectedTreeId
      ? findTreeNodeById(tsoDeviceOptions[loggedInTsoId ?? -1] ?? [], selectedTreeId)
      : null;

    setValue('measurementDeviceId', selectedNode?.entityId ?? null, { shouldValidate: true, shouldDirty: true });
    trigger('measurementDeviceId');
  };

  const aggregationIntervalChangeHandler = (selectedOption: { id: any }) => {
    const numericValue = selectedOption?.id ?? null;

    if (numericValue !== null) {
      setValue('aggregationIntervalId', numericValue, { shouldValidate: true, shouldDirty: true });
      trigger('aggregationIntervalId');
    } else {
      setValue('aggregationIntervalId', null, { shouldValidate: true, shouldDirty: true });
    }
  };

  useEffect(() => {
    trigger('aggregationIntervalId');
  }, [trigger]);

  return (
    <StyledUploadDetailValidityCheckAdditionalContentErrorDetailsMissingWrapper>
      <FormFieldTsoPq
        label={t('upload.detailedView.measurementDevice')}
        placeholder={t('general.error.valueSelect')}
        name="measurementDeviceId"
        type="treeAutocomplete"
        errorMessage={'general.error.valueSelect'}
        register={register}
        multiple={false}
        watch={watch}
        setValue={setValue}
        errors={errors}
        autocompleteOptions={(loggedInTsoId ? tsoDeviceOptions[loggedInTsoId] : undefined) ?? []}
        customWidth="34rem"
        required
        handleChange={deviceChangeHandler}
        defaultValue={selectedDeviceTreeId}
        trigger={trigger}
      ></FormFieldTsoPq>

      <FormFieldTsoPq
        label="upload.detailedView.aggregationInterval"
        placeholder="upload.detailedView.pleaseSelect"
        name="aggregationIntervalId"
        type="singleSelect"
        errorMessage="general.error.valueSelect"
        register={register}
        watch={watch}
        setValue={setValue}
        errors={errors}
        options={
          aggregationIntervals?.externalDtos?.map((x) => ({
            id: x.id as number,
            label: t(`upload.${x.name}`),
            value: x.id as number,
          })) ?? []
        }
        customWidth="34rem"
        required
        handleChange={aggregationIntervalChangeHandler}
        trigger={trigger}
      />
    </StyledUploadDetailValidityCheckAdditionalContentErrorDetailsMissingWrapper>
  );
};

const ValidityCheckDetails = ({
  validationErrorDetails,
  errorReason,
  selectedMeasurementDeviceId,
  selectedAggregationIntervalId,
  onSubmitHandler,
}: ValidityCheckDetailsProps) => {
  const dispatch = useAppDispatch();

  const [importDataPartialy] = useLazyPostImportOperationStartPartialImportQuery();
  const [importDataOverwrite] = useLazyPostImportOperationStartForceFullImportQuery();
  const [retryValidation] = usePostImportOperationRetryImportValidationMutation();
  const selectedImportOperationId = useAppSelector((state) => state.uploadedData.selectedImportOperationId);
  const [errorCounts, seterrorCounts] = useState(0);
  const [open, setOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const validationSchema = object({
    measurementDeviceId: mandatorySelection,
    aggregationIntervalId: mandatoryIntervalOption,
  });

  useEffect(() => {
    const validationErrorDetailsSplit =
      validationErrorDetails
        ?.split(',')
        .map((p) => p.trim())
        .filter((p) => p !== '') || [];
    seterrorCounts(validationErrorDetailsSplit.length);
  }, [validationErrorDetails]);

  const openModal = () => {
    setOpen(true);
  };
  const closeModal = () => {
    setOpen(false);
  };

  const handleOverwriteData = async () => {
    closeModal();
    try {
      await importDataOverwrite(Number(selectedImportOperationId)).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.validity.overwrite.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error(error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.validity.overwrite.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handlePartialDataImport = async () => {
    closeModal();
    try {
      await importDataPartialy(Number(selectedImportOperationId)).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.validity.keep.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error(error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.validity.keep.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handleRetryDataValidation = async () => {
    try {
      await retryValidation(Number(selectedImportOperationId)).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.validity.tryAgain.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error(error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.validity.tryAgain.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const dataUpdatetext = t('upload.detailedView.validityCheckDetails.dataAlreadyUploadedInTotal', {
    count: errorCounts,
  });
  const dataUpdateModaltext = t('upload.detailedView.validityCheckDetails.dataAlreadyUploadedModalContent', {
    count: errorCounts,
  });

  return (
    errorReason && (
      <ValidityCheckDetailsContainer isRow={errorReason.id === 7}>
        <StyledBoxContainer>
          <TypographyTsoPq
            color={tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled}
            typographyType="sectionTitleSMBold"
          >
            {errorReason?.title}
          </TypographyTsoPq>
          <TypographyTsoPq typographyType="sectionTitleSM" whiteSpace={'pre-line'}>
            {errorReason?.content}
            {errorReason.id === 7 && (
              <TypographyTsoPq
                component="span"
                dangerouslySetInnerHTML={{ __html: dataUpdatetext }}
                sx={{
                  '& strong': { fontWeight: 'bold' },
                }}
                typographyType={'sectionTitleSM'}
              />
            )}
          </TypographyTsoPq>
        </StyledBoxContainer>
        {validationErrorDetails && (
          <TypographyTsoPq typographyType="bodyTextSM" sx={{ wordWrap: 'break-word', wordBreak: 'break-all' }}>
            {errorReason.id === 7 || errorReason.id === 8 ? ' ' : validationErrorDetails}
          </TypographyTsoPq>
        )}
        {errorReason.id === 8 && (
          <StyledBoxContainerButtons>
            <ButtonTsoPq
              buttonType="filledPrimary"
              textContent={t('upload.detailedView.validityCheckDetails.error.button.tryAgain')}
              onClick={() => handleRetryDataValidation()}
            />
          </StyledBoxContainerButtons>
        )}
        <Box>
          {errorReason.id === 7 && (
            <StyledBoxContainerButtons>
              <ButtonTsoPq
                buttonType="outlinedPrimary"
                textContent={t('upload.detailedView.validityCheckDetails.error.button.noOverwrite')}
                onClick={() => handlePartialDataImport()}
              />
              <ButtonTsoPq
                buttonType="filledPrimary"
                textContent={t('upload.detailedView.validityCheckDetails.error.button.overwrite')}
                onClick={() => openModal()}
              />
            </StyledBoxContainerButtons>
          )}
        </Box>
        <ModalDialogTsoPq modalState={open} customMaxWidth="40.325rem" icon={true} onClose={() => setOpen(false)}>
          <TypographyTsoPq typographyType="sectionTitleMDBold">
            {t('upload.detailedView.validityCheckDetails.dataAlreadyUploadedModalTitel')}
          </TypographyTsoPq>
          <Stack direction="row" spacing={2}>
            <TypographyTsoPq
              component="span"
              whiteSpace={'pre-line'}
              dangerouslySetInnerHTML={{ __html: dataUpdateModaltext }}
              sx={{
                '& strong': { fontWeight: 'bold' },
              }}
              typographyType={'sectionTitleSM'}
            />
          </Stack>
          <StyledBoxContainerButtons>
            <ButtonTsoPq
              buttonType="outlinedPrimary"
              textContent={t('management.general.cancel')}
              onClick={() => closeModal()}
            />
            <ButtonTsoPq
              buttonType="filledPrimary"
              textContent={t('upload.detailedView.validityCheckDetails.error.button.overwrite')}
              onClick={() => handleOverwriteData()}
            />
          </StyledBoxContainerButtons>
        </ModalDialogTsoPq>
        {(errorReason.id === 1 || errorReason.id === 2) && (
          <>
            <StyledUploadDetailValidityCheckAdditionalContentErrorDetailsContent>
              {t('upload.detailedView.validityCheckDetails.deviceAndAggregation.measuringDevice')}
            </StyledUploadDetailValidityCheckAdditionalContentErrorDetailsContent>
            <StyledUploadDetailValidityCheckAdditionalContentErrorDetailsContent>
              {t('upload.detailedView.validityCheckDetails.deviceAndAggregation.aggregation')}
            </StyledUploadDetailValidityCheckAdditionalContentErrorDetailsContent>
            <FormTsoPq
              defaultValues={{
                measurementDeviceId: selectedMeasurementDeviceId ?? undefined,
                aggregationIntervalId: selectedAggregationIntervalId ?? undefined,
              }}
              onSubmit={(data) =>
                onSubmitHandler(
                  data.measurementDeviceId ?? selectedMeasurementDeviceId ?? null,
                  data.aggregationIntervalId ?? selectedAggregationIntervalId ?? null
                )
              }
              discardChanges={() => {}}
              withoutDiscard
              schema={validationSchema}
              customProps={{ selectedMeasurementDeviceId }}
            >
              {FormContent}
            </FormTsoPq>
          </>
        )}
      </ValidityCheckDetailsContainer>
    )
  );
};

export default ValidityCheckDetails;
